import React from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '@/shared/hooks/usePageTitle';
import PolicyPage, { SupportAddress } from './PolicyPage';

/**
 * What the exchange collects, who sees it, where it is kept, and for how long.
 *
 * Every statement here is a claim about the running system. When a field is
 * added to a model, a processor is added, or a retention setting changes
 * (the log group's RetentionInDays in scripts/aws/ecs-cluster.yml, the
 * owner projections in the listing controller), this page changes with it.
 */
const TITLE = 'Privacy statement';

function PrivacyPage() {
    usePageTitle(TITLE);
    return (
        <PolicyPage
            title={TITLE}
            summary={
                <>
                    The exchange collects what it needs to move furniture
                    between departments: who you are, what you post, and what
                    you ask for. It does not sell your information, show
                    advertising, or use tracking or analytics tools.
                </>
            }
        >
            <h2>Who runs the exchange</h2>
            <p>
                The Auraria Furniture Exchange is a pilot for the Auraria
                Sustainable Campus Program partners: Metropolitan State
                University of Denver, the University of Colorado Denver, the
                Community College of Denver, and the Auraria Higher Education
                Center. It is built and hosted by the Community-Centered
                Computing (C3) Lab in the Department of Computer Sciences at MSU
                Denver. In this statement, &ldquo;we&rdquo; means the C3 Lab and
                the coordinators the partner institutions name to run the
                exchange.
            </p>

            <h2>What we collect</h2>
            <p>
                <strong>Your account.</strong> Your campus email address, your
                name, your department, and optionally a phone number. Your
                institution is derived from your email address. Google Firebase
                Authentication handles your password; the exchange never
                receives or stores it. We also record whether you want the
                weekly digest, your role on the site, and when you last signed
                in.
            </p>
            <p>
                <strong>What you post.</strong> Listing details, including the
                title, description, category, condition, quantity, dimensions,
                building and room, pickup notes, estimated value and weight,
                whether the item is capital equipment, and any photographs you
                upload.
            </p>
            <p>
                <strong>What you ask for.</strong> Your request note, pickup
                window, delivery destination, the messages you exchange with the
                other department, and the dates each request was made and
                decided.
            </p>
            <p>
                <strong>Technical records.</strong> The server logs each request
                it receives, including your IP address, browser type, and the
                page or action requested. These logs exist to keep the service
                running and secure.
            </p>
            <p>
                We do not collect sensitive personal information, and we do not
                collect information about you from other sources.
            </p>

            <h2>How we use it</h2>
            <ul>
                <li>
                    To create your account and confirm you work at one of the
                    four institutions.
                </li>
                <li>
                    To show your listings to other members and let them ask for
                    an item.
                </li>
                <li>
                    To send the notifications a transfer depends on: a new
                    request, an acceptance or decline, a completed transfer, a
                    listing about to expire. And, unless you turn it off on your
                    profile, one weekly digest of newly posted items.
                </li>
                <li>
                    To produce reuse reports for the partner institutions. These
                    reports count items and transfers and name no one.
                </li>
                <li>To keep the service secure and to investigate misuse.</li>
            </ul>

            <h2>Who can see your information</h2>
            <ul>
                <li>
                    <strong>Other members</strong> see your name, institution,
                    and department on your listings. When they open a listing
                    they also see your email address and, if you gave one, your
                    phone number, so they can arrange a pickup.
                </li>
                <li>
                    <strong>The other party to a request</strong> sees your
                    request note and the messages in that request&rsquo;s
                    thread. No one else reads message text, including
                    coordinators browsing the list of requests.
                </li>
                <li>
                    <strong>Facilities offices.</strong> When a request is
                    accepted, the facilities office for the building, and the
                    office for the requesting institution, receive a copy of the
                    acceptance so they can plan the move.
                </li>
                <li>
                    <strong>Coordinators</strong> named by the partner
                    institutions can see member accounts, every listing, and
                    every request, so they can answer questions and remove
                    listings that break the{' '}
                    <Link to="/terms">terms of use</Link>.
                </li>
                <li>
                    <strong>Our service providers</strong> store and process
                    information only to run the exchange: Google Firebase
                    (sign-in), Amazon Web Services (hosting, photo storage,
                    email delivery, and logs), and MongoDB Atlas (the database).
                    All of it is stored in the United States.
                </li>
            </ul>
            <p>
                We do not sell or rent personal information, and we do not share
                it for advertising. We may disclose information when the law
                requires it. The partner institutions are public bodies, so
                records held for the exchange may be subject to the Colorado
                Open Records Act.
            </p>

            <h2>Cookies and tracking</h2>
            <p>
                The exchange uses no analytics, advertising, or third-party
                tracking. Your browser keeps a sign-in token from Firebase so
                you stay signed in; signing out removes it. We do not respond to
                Do Not Track signals because there is no tracking for them to
                turn off.
            </p>

            <h2>How long we keep it</h2>
            <ul>
                <li>Server logs are deleted automatically after 14 days.</li>
                <li>
                    Your account, listings, and requests are kept while the
                    pilot runs, because completed transfers are the record the
                    reuse reports are built from.
                </li>
                <li>
                    When you ask us to delete your account, we delete your
                    sign-in account and remove your name, department, and phone
                    number from our records, along with your open listings and
                    their photographs. Completed transfers are kept without your
                    name so the reports stay accurate.
                </li>
                <li>
                    If the pilot ends without continuing, we delete the
                    database, photographs, and sign-in accounts.
                </li>
            </ul>

            <h2>How we protect it</h2>
            <p>
                The site is served only over HTTPS. Photographs and the database
                are encrypted at rest. The application reaches the database with
                short-lived cloud credentials rather than a stored password, and
                only coordinators can reach the administrative area. No system
                is perfectly secure, and we cannot guarantee that information
                will never be accessed without authorization. If we learn of a
                breach affecting your information, we will tell you.
            </p>

            <h2>Student information</h2>
            <p>
                The exchange is for faculty and staff moving university
                property. It is not designed to hold student education records.
                Do not put student names, grades, or other records in a listing,
                a photograph, or a message. It is not directed at anyone under
                18.
            </p>

            <h2>Your choices</h2>
            <ul>
                <li>
                    Update your name, department, phone number, and digest
                    preference on your profile at any time.
                </li>
                <li>Withdraw or remove your own listings and requests.</li>
                <li>
                    Ask us for a copy of the information we hold about you, to
                    correct it, or to delete your account, by writing to{' '}
                    <SupportAddress />.
                </li>
            </ul>

            <h2>Changes to this statement</h2>
            <p>
                When this statement changes, we update the date at the top of
                the page. For a significant change, we will also tell members by
                email.
            </p>

            <h2>Contact</h2>
            <p>
                Questions about this statement or your information go to{' '}
                <SupportAddress />.
            </p>
        </PolicyPage>
    );
}

export default PrivacyPage;
